import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Flex,
  Card,
  Text,
  Box,
  Container,
} from '@radix-ui/themes';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeftIcon } from '@radix-ui/react-icons';

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Sophisticated financial-themed animated background
  useEffect(() => {
    const canvas = document.getElementById(
      'forgot-password-bg',
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Dark financial gradient with subtle movement
    let time = 0;
    const animate = () => {
      time += 0.005;
      const gradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        canvas.height,
      );

      // Dark financial color palette
      gradient.addColorStop(
        0,
        `hsl(${220 + Math.sin(time) * 10}, 25%, ${8 + Math.sin(time * 0.3) * 2}%)`,
      );
      gradient.addColorStop(
        0.3,
        `hsl(${240 + Math.cos(time * 0.4) * 8}, 30%, ${12 + Math.cos(time * 0.2) * 3}%)`,
      );
      gradient.addColorStop(
        0.7,
        `hsl(${260 + Math.sin(time * 0.6) * 12}, 35%, ${15 + Math.sin(time * 0.4) * 2}%)`,
      );
      gradient.addColorStop(
        1,
        `hsl(${280 + Math.cos(time * 0.5) * 15}, 40%, ${18 + Math.cos(time * 0.3) * 3}%)`,
      );

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('/auth/password-reset/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to send password reset email');
      }

      setIsSubmitted(true);
      toast({
        title: 'Check your email',
        description:
          'If an account with that email exists, a password reset link has been sent.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Box
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: '20px',
        }}
      >
        <canvas
          id="forgot-password-bg"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
          }}
        />

        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
              linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: -1,
          }}
        />

        <Container
          size="3"
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: '480px',
          }}
        >
          <Card
            style={{
              width: '100%',
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow:
                '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
              borderRadius: '20px',
            }}
          >
            <Flex direction="column" gap="6" p="8">
              <Flex direction="column" align="center" gap="4">
                <Box
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '18px',
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    color: 'white',
                    boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
                  }}
                >
                  ✉️
                </Box>
                <Text
                  size="7"
                  weight="bold"
                  align="center"
                  style={{
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Check Your Email
                </Text>
                <Text
                  size="3"
                  style={{ color: '#e2e8f0 !important', textAlign: 'center' }}
                >
                  If an account with that email exists, we've sent a password
                  reset link to <strong>{email}</strong>
                </Text>
                <Text
                  size="2"
                  style={{
                    color: '#94a3b8 !important',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}
                >
                  The link will expire in 1 hour for security reasons.
                </Text>
              </Flex>

              <Button
                type="button"
                size="4"
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  height: '56px',
                  borderRadius: '12px',
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#f8fafc',
                  cursor: 'pointer',
                  boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)',
                }}
              >
                Back to Login
              </Button>
            </Flex>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: '20px',
      }}
    >
      <canvas
        id="forgot-password-bg"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }}
      />

      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: -1,
        }}
      />

      <Box
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background:
            'radial-gradient(circle, rgba(139, 92, 246, 0.03) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: -1,
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '200px',
          height: '200px',
          background:
            'radial-gradient(circle, rgba(16, 185, 129, 0.02) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: -1,
        }}
      />

      <Container
        size="3"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '480px',
        }}
      >
        <Card
          style={{
            width: '100%',
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow:
              '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            borderRadius: '20px',
          }}
        >
          <Flex direction="column" gap="6" p="8">
            <Flex direction="column" align="center" gap="4">
              <Box
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '18px',
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  color: 'white',
                  boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)',
                }}
              >
                🔑
              </Box>
              <Text
                size="7"
                weight="bold"
                align="center"
                style={{
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Forgot Password?
              </Text>
              <Text
                size="3"
                style={{ color: '#e2e8f0 !important', textAlign: 'center' }}
              >
                No worries! Enter your email and we'll send you reset
                instructions.
              </Text>
            </Flex>

            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap="5">
                <Box>
                  <Text
                    size="3"
                    weight="medium"
                    mb="3"
                    as="div"
                    style={{ color: '#e2e8f0 !important' }}
                  >
                    Email Address
                  </Text>
                  <TextField.Root
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your email address"
                    required
                    disabled={isLoading}
                    style={{
                      height: '52px',
                      borderRadius: '12px',
                      border:
                        focusedField === 'email'
                          ? '2px solid #8b5cf6'
                          : '1px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      padding: '0 16px',
                      background:
                        focusedField === 'email'
                          ? 'rgba(139, 92, 246, 0.08)'
                          : 'rgba(255, 255, 255, 0.08)',
                      color: '#f1f5f9',
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  size="4"
                  disabled={isLoading || !email}
                  style={{
                    width: '100%',
                    height: '56px',
                    borderRadius: '12px',
                    background: isLoading
                      ? 'linear-gradient(135deg, #475569 0%, #334155 100%)'
                      : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isLoading ? '#cbd5e1' : '#f8fafc',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: isLoading
                      ? 'none'
                      : '0 12px 40px rgba(139, 92, 246, 0.4)',
                    transform: isLoading ? 'scale(0.98)' : 'scale(1)',
                  }}
                >
                  {isLoading ? (
                    <Flex align="center" gap="3">
                      <Box
                        style={{
                          width: '18px',
                          height: '18px',
                          border: '2px solid rgba(203, 213, 225, 0.3)',
                          borderTop: '2px solid #cbd5e1',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                      Sending reset link...
                    </Flex>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </Flex>
            </form>

            <Flex justify="center" pt="3">
              <Button
                type="button"
                variant="ghost"
                size="3"
                onClick={() => navigate('/login')}
                style={{
                  color: '#8b5cf6',
                  textDecoration: 'none',
                  padding: '6px 12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <ArrowLeftIcon style={{ width: 16, height: 16 }} />
                Back to Login
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Container>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        input::placeholder {
          color: #94a3b8 !important;
        }

        input:focus {
          outline: none;
        }

        [data-radix-text] {
          color: #e2e8f0 !important;
        }
      `}</style>
    </Box>
  );
}
